import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import bankService from "../../../services/bank.service";
import { formatVehicleNumberDisplay } from "../../../utils/vehicleNumberUtils";
import CaseLocationsMapView from "../../../components/tracking/CaseLocationsMapView";
import AdminExcelFieldGrid from "../../../components/repo/AdminExcelFieldGrid";
import InventoryFilesGallery from "../../repo/confirmation/InventoryFilesGallery";
import ParkingYardSavedDetails from "../../repo/inventory/ParkingYardSavedDetails";
import "../../../styles/bankDetails.css";
import "../../../styles/confirmation.css";
import "../../../styles/inventoryUpdate.css";
import "../../../styles/vehicles.css";
import "../../../styles/bankTraceCases.css";

const OMIT_CASE_CODE = new Set(["Case Code"]);

function formatTracedAt(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function BankTraceCaseDetail() {
  const { id } = useParams();
  const location = useLocation();
  const fromSeized = location.pathname.includes("/seized-vehicles");
  const backTo = fromSeized ? "/bank/seized-vehicles" : "/bank/trace-cases";
  const backLabel = fromSeized ? "Seized vehicles" : "Trace Cases";

  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    bankService
      .getTraceCase(id)
      .then((res) => {
        if (cancelled) return;
        setRow(res?.data?.data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || "Trace case not found.");
        setRow(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const confirmation = row?.confirmation || null;
  const seized = Boolean(row?.seized || confirmation?.inventoryConfirmedAt);
  const caseData = useMemo(() => {
    if (row?.case) return row.case;
    if (!row) return null;
    return {
      vehicleNumber: row.vehicleNumber,
      customerName: row.customerName,
      loanAccountNumber: row.loanNumber,
    };
  }, [row]);

  const fields = row
    ? [
        ["Vehicle Number", formatVehicleNumberDisplay(row.vehicleNumber)],
        ["Loan Number", row.loanNumber || "—"],
        ["Customer Name", row.customerName || "—"],
        ["Trace By", row.traceBy || "—"],
        ["Trace Location", row.traceLocation || "—"],
        ["Trace Date & Time", formatTracedAt(row.tracedAt)],
        ["Status", row.status || "Traced"],
      ]
    : [];

  const lat = Number(row?.latitude);
  const lng = Number(row?.longitude);
  const hasGps = Number.isFinite(lat) && Number.isFinite(lng);
  const mapsUrl = String(row?.mapsUrl || "").trim();

  return (
    <div className="bd-page cf-page bank-trace-detail">
      <p>
        <Link to={backTo} className="secondary-page-btn bank-trace-detail__back">
          <FiArrowLeft aria-hidden /> Back to {backLabel}
        </Link>
      </p>
      <h2>{seized ? "Seized vehicle" : "Trace Case"}</h2>

      {loading ? <p className="cf-excel-loading">Loading case…</p> : null}
      {error ? <p className="bd-feedback bd-error">{error}</p> : null}

      {!loading && row && !seized ? (
        <dl className="bank-trace-detail__dl">
          {fields.map(([label, value]) => (
            <div key={label} className="bank-trace-detail__row">
              <dt>{label}</dt>
              <dd>
                {label === "Trace Location" ? (
                  <span className="bank-trace-detail__location">
                    <span>{value}</span>
                    {mapsUrl ? (
                      <a
                        className="bank-trace-detail__maps"
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View on Map
                      </a>
                    ) : null}
                  </span>
                ) : (
                  value
                )}
              </dd>
            </div>
          ))}
          {hasGps ? (
            <div className="bank-trace-detail__row">
              <dt>Map</dt>
              <dd>
                <div className="bank-trace-detail__map">
                  <CaseLocationsMapView
                    locations={[{ latitude: lat, longitude: lng, createdAt: row.tracedAt }]}
                  />
                </div>
              </dd>
            </div>
          ) : null}
        </dl>
      ) : null}

      {!loading && row && seized ? (
        <div className="bank-seized-detail">
          <section className="card bank-seized-detail__section">
            <h3>Vehicle details</h3>
            <p className="muted bank-seized-detail__meta">
              {row.traceBy ? `Seized by ${row.traceBy}` : null}
              {row.status ? ` · ${row.status}` : null}
              {row.tracedAt ? ` · ${formatTracedAt(row.tracedAt)}` : null}
            </p>
            <AdminExcelFieldGrid
              caseData={caseData}
              loading={false}
              layout="cards"
              omitLabels={OMIT_CASE_CODE}
            />
          </section>

          {confirmation?.parkingYardName || confirmation?.parkingYardId ? (
            <section className="card bank-seized-detail__section">
              <ParkingYardSavedDetails confirmation={confirmation} />
            </section>
          ) : null}

          <section className="card bank-seized-detail__section">
            <h3>Photos, videos & files</h3>
            <p className="muted">
              Inventory submitted{" "}
              {confirmation?.inventorySubmittedAt
                ? formatTracedAt(confirmation.inventorySubmittedAt)
                : "—"}
              {confirmation?.inventoryConfirmedAt
                ? ` · Confirmed ${formatTracedAt(confirmation.inventoryConfirmedAt)}`
                : ""}
              .
            </p>
            <InventoryFilesGallery confirmation={confirmation} />
          </section>

          {hasGps || mapsUrl ? (
            <section className="card bank-seized-detail__section">
              <h3>Trace location</h3>
              {row.traceLocation ? <p>{row.traceLocation}</p> : null}
              {mapsUrl ? (
                <p>
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                    View on Map
                  </a>
                </p>
              ) : null}
              {hasGps ? (
                <div className="bank-trace-detail__map">
                  <CaseLocationsMapView
                    locations={[{ latitude: lat, longitude: lng, createdAt: row.tracedAt }]}
                  />
                </div>
              ) : null}
            </section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
